// src/modules/app-views/services/pages/Document/tabs/AiInfoTab/ReferencesSection.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Tag, Typography, Collapse, theme } from 'antd';
import {
  BookOutlined,
  PlayCircleOutlined,
  GlobalOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import { DEFAULT_OPEN_KEYS } from './index';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * ReferencesSection - Displays technical references from AI cataloging
 * Each subsection (Videos, Websites, Search Terms) is a Collapse panel
 */
const ReferencesSection = ({ aiResponse, isMobile }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const refs = aiResponse?.references;
  if (!refs) return null;

  const hasContent = refs.videos?.length || refs.websites?.length || refs.searchTerms?.length;
  if (!hasContent) return null;

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Videos */}
      {refs.videos?.length > 0 && (
        <Collapse
          className="collapsed-content-fit"

          defaultActiveKey={DEFAULT_OPEN_KEYS.includes('videos') ? ['videos'] : []}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'videos',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <PlayCircleOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('recommended_videos')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  {refs.videos.map((video, i) => (
                    <div
                      key={i}
                      style={{
                        marginBottom: i < refs.videos.length - 1 ? 12 : 0,
                        padding: '10px 12px',
                        borderRadius: 8,
                        backgroundColor: 'var(--ant-color-fill-quaternary)',
                      }}
                    >
                      {video.url ? (
                        <a href={video.url} target="_blank" rel="noopener noreferrer">
                          <Text strong style={{ fontSize: 13 }}>{video.title}</Text>
                        </a>
                      ) : (
                        <Text strong style={{ fontSize: 13 }}>{video.title}</Text>
                      )}
                      {video.channel && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
                          {video.channel}
                        </Text>
                      )}
                      {video.topic && (
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
                          {video.topic}
                        </Text>
                      )}
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Websites */}
      {refs.websites?.length > 0 && (
        <Collapse
          className="collapsed-content-fit"

          defaultActiveKey={DEFAULT_OPEN_KEYS.includes('websites') ? ['websites'] : []}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'websites',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <GlobalOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('reference_websites')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  {refs.websites.map((site, i) => (
                    <div
                      key={i}
                      style={{
                        marginBottom: i < refs.websites.length - 1 ? 12 : 0,
                        padding: '10px 12px',
                        borderRadius: 8,
                        backgroundColor: 'var(--ant-color-fill-quaternary)',
                      }}
                    >
                      {site.url ? (
                        <a href={site.url} target="_blank" rel="noopener noreferrer">
                          <Text strong style={{ fontSize: 13 }}>{site.name}</Text>
                        </a>
                      ) : (
                        <Text strong style={{ fontSize: 13 }}>{site.name}</Text>
                      )}
                      {site.description && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
                          {site.description}
                        </Text>
                      )}
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Search Terms */}
      {refs.searchTerms?.length > 0 && (
        <Collapse
          className="collapsed-content-fit"

          defaultActiveKey={DEFAULT_OPEN_KEYS.includes('search-terms') ? ['search-terms'] : []}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'search-terms',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <SearchOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('search_terms')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  <Flex gap={4} wrap="wrap">
                    {refs.searchTerms.map((term, i) => (
                      <Tag key={i} color="processing">{term}</Tag>
                    ))}
                  </Flex>
                </div>
              ),
            },
          ]}
        />
      )}
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ReferencesSection;
